import { useState, useCallback } from 'react';
import { message } from 'antd';
import { adminAPI } from './api';

export interface ParentRegistration {
  id: number;
  phone: string;
  remark: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  reviewedAt?: string;
  reviewedByAdmin?: string;
  reviewRemark?: string;
}

export const useParentRegistrations = () => {
  const [registrations, setRegistrations] = useState<ParentRegistration[]>([]);
  const [pendingRegistrations, setPendingRegistrations] = useState<ParentRegistration[]>([]);
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 获取所有注册申请
  const fetchAllRegistrations = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await adminAPI.getAllParentRegistrations();
      setRegistrations(response.data);
      return response.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || '获取家长注册申请失败';
      setError(errorMsg);
      message.error(errorMsg);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // 获取待审核的注册申请
  const fetchPendingRegistrations = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await adminAPI.getPendingParentRegistrations();
      setPendingRegistrations(response.data);
      return response.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || '获取待审核的家长注册申请失败';
      setError(errorMsg);
      message.error(errorMsg);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // 审核家长注册申请
  const reviewRegistration = useCallback(async (
    id: number, 
    status: 'APPROVED' | 'REJECTED', 
    reviewRemark?: string
  ) => {
    setReviewing(true);
    setError(null);
    
    try {
      const response = await adminAPI.reviewParentRegistration(id, { status, reviewRemark });
      
      // 更新本地数据
      setRegistrations(prev => 
        prev.map(reg => reg.id === id ? response.data : reg)
      );
      setPendingRegistrations(prev => 
        prev.filter(reg => reg.id !== id)
      );
      
      message.success(`家长注册申请已${status === 'APPROVED' ? '通过' : '拒绝'}`);
      return response.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || '审核家长注册申请失败';
      setError(errorMsg);
      message.error(errorMsg);
      return null;
    } finally {
      setReviewing(false);
    }
  }, []);

  return {
    registrations,
    pendingRegistrations,
    loading,
    reviewing,
    error,
    fetchAllRegistrations,
    fetchPendingRegistrations,
    reviewRegistration
  };
}; 